import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import Login from '~/pages/Login';
import MFA from '~/pages/MFA';
import RequireAuth from '~/components/RequireAuth';
import NotFound from '~/components/NotFound';

// Statically imported: Login + the SPA shell live in the main bundle.
// Everything under pages/admin/* is lazy-loaded so it ends up in the
// admin-* chunk (which the edge gate refuses to serve unauthenticated).
// See docs/SECURITY_ADMIN_INSTALLER.md.
const Dashboard = lazy(() => import('~/pages/admin/Dashboard'));
const ResetPassword = lazy(() => import('~/pages/ResetPassword'));
const Products = lazy(() => import('~/pages/admin/products/index'));
const ProductDetail = lazy(() => import('~/pages/admin/products/$productId'));
const Orders = lazy(() => import('~/pages/admin/orders/index'));
const OrderDetail = lazy(() => import('~/pages/admin/orders/$orderId'));
const Customers = lazy(() => import('~/pages/admin/customers/index'));
const CustomerDetail = lazy(() => import('~/pages/admin/customers/$customerId'));
const Installations = lazy(() => import('~/pages/admin/installations/index'));
const Installers = lazy(() => import('~/pages/admin/installers/index'));
const Brands = lazy(() => import('~/pages/admin/brands/index'));
const Resellers = lazy(() => import('~/pages/admin/resellers/index'));
const DiscountCodes = lazy(() => import('~/pages/admin/discount-codes/index'));
const Recipients = lazy(() => import('~/pages/admin/recipients/index'));
const Users = lazy(() => import('~/pages/admin/users/index'));
const AuditLogs = lazy(() => import('~/pages/admin/audit-logs/index'));
const FAQs = lazy(() => import('~/pages/admin/faqs/index'));
const System = lazy(() => import('~/pages/admin/system/index'));
const SystemRequirements = lazy(() => import('~/pages/admin/system-requirements/index'));
const ExpertGuide = lazy(() => import('~/pages/admin/expert-guide/index'));
const CatalogExpansion = lazy(() => import('~/pages/admin/catalog-expansion/index'));
const FormsComplaints = lazy(() => import('~/pages/admin/forms/complaints/index'));
const FormsCancellations = lazy(() => import('~/pages/admin/forms/cancellations/index'));
const FormsJobApplications = lazy(() => import('~/pages/admin/forms/job-applications/index'));
const FormsEmailSubscribers = lazy(() => import('~/pages/admin/forms/email-subscribers/index'));
const FormsSurveyResponses = lazy(() => import('~/pages/admin/forms/survey-responses/index'));
const MarketingQRCodes = lazy(() => import('~/pages/admin/marketing/qr-codes/index'));
const SettingsInstallerSchedules = lazy(() => import('~/pages/admin/settings/installer-schedules/index'));
const SettingsProductLines = lazy(() => import('~/pages/admin/settings/product-lines/index'));
const AdminProviders = lazy(() => import('~/pages/admin/AdminProviders'));

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="p-6 text-gray-500">Loading…</div>}>{children}</Suspense>
);

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/reset-password', element: <Lazy><ResetPassword /></Lazy> },
  // /mfa is the gate between AAL1 (password-only) and AAL2 (MFA-verified).
  // It needs an authenticated AAL1 session to operate, so it sits OUTSIDE
  // RequireAuth (which requires AAL2). The MFA component itself bounces
  // anonymous visitors to /login.
  { path: '/mfa', element: <MFA /> },
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Lazy><AdminProviders /></Lazy>,
        children: [
          { path: '/dashboard', element: <Lazy><Dashboard /></Lazy> },
          { path: '/products', element: <Lazy><Products /></Lazy> },
          { path: '/products/:productId', element: <Lazy><ProductDetail /></Lazy> },
          { path: '/orders', element: <Lazy><Orders /></Lazy> },
          { path: '/orders/:orderId', element: <Lazy><OrderDetail /></Lazy> },
          { path: '/customers', element: <Lazy><Customers /></Lazy> },
          { path: '/customers/:customerId', element: <Lazy><CustomerDetail /></Lazy> },
          { path: '/installations', element: <Lazy><Installations /></Lazy> },
          { path: '/installers', element: <Lazy><Installers /></Lazy> },
          { path: '/brands', element: <Lazy><Brands /></Lazy> },
          { path: '/resellers', element: <Lazy><Resellers /></Lazy> },
          { path: '/discount-codes', element: <Lazy><DiscountCodes /></Lazy> },
          { path: '/recipients', element: <Lazy><Recipients /></Lazy> },
          { path: '/users', element: <Lazy><Users /></Lazy> },
          { path: '/audit-logs', element: <Lazy><AuditLogs /></Lazy> },
          { path: '/faqs', element: <Lazy><FAQs /></Lazy> },
          { path: '/system', element: <Lazy><System /></Lazy> },
          { path: '/system-requirements', element: <Lazy><SystemRequirements /></Lazy> },
          { path: '/expert-guide', element: <Lazy><ExpertGuide /></Lazy> },
          { path: '/catalog-expansion', element: <Lazy><CatalogExpansion /></Lazy> },
          { path: '/forms/complaints', element: <Lazy><FormsComplaints /></Lazy> },
          { path: '/forms/cancellations', element: <Lazy><FormsCancellations /></Lazy> },
          { path: '/forms/job-applications', element: <Lazy><FormsJobApplications /></Lazy> },
          { path: '/forms/email-subscribers', element: <Lazy><FormsEmailSubscribers /></Lazy> },
          { path: '/forms/survey-responses', element: <Lazy><FormsSurveyResponses /></Lazy> },
          { path: '/marketing/qr-codes', element: <Lazy><MarketingQRCodes /></Lazy> },
          { path: '/settings/installer-schedules', element: <Lazy><SettingsInstallerSchedules /></Lazy> },
          { path: '/settings/product-lines', element: <Lazy><SettingsProductLines /></Lazy> },
        ],
      },
    ],
  },
  // Catch-all 404. Visible error beats silent redirect — when a developer
  // introduces a broken URL, this surfaces the bug instead of bouncing the
  // user to /dashboard (which previously masked things like
  // `navigate(`/admin/products/${id}`)` working on bidetny.com/admin/* but
  // 404'ing on admin.bidetny.com).
  { path: '*', element: <NotFound /> },
]);
